import React, { useState, useMemo } from 'react';
import Option from './Option';
import { dariAlphabet } from './dariAlphabet';
import { shapeOf, formsFor, positionAt, POSITION_LABELS } from './letterForms';
import { pickExample } from './letterExamples';
import { ExampleWord } from './LetterCard';
import { audioUrlFor, playLoud } from './audioStorage';

const MAX_QUESTIONS = 10;

function shuffle(arr) {
  return [...arr].sort(() => Math.random() - 0.5);
}

function sample(pool, n, excludeKey, keyFn = (x) => x) {
  const seen = new Set([excludeKey]);
  const out = [];
  for (const item of shuffle(pool)) {
    const k = keyFn(item);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(item);
    if (out.length === n) break;
  }
  return out;
}

function infoFor(dari) {
  return dariAlphabet.find((l) => l.dari === dari);
}

// Build a varied question pool from the lesson's letters.
function buildQuestions(letterChars, recordedSet) {
  const letters = letterChars.map(infoFor).filter(Boolean);
  const pool = [];

  for (const L of letters) {
    // 1. Name from glyph
    pool.push({
      type: 'name',
      promptText: 'What is this letter called?',
      glyph: L.dari,
      options: shuffle([
        { label: L.english, isCorrect: true },
        ...sample(dariAlphabet, 3, L.english, (x) => x.english).map((x) => ({ label: x.english }))
      ])
    });

    // 2. Glyph from name
    pool.push({
      type: 'glyph',
      promptText: `Which letter is “${L.english}” (${L.sound})?`,
      options: shuffle([
        { label: L.dari, dir: 'rtl', isCorrect: true },
        ...sample(dariAlphabet, 3, L.dari, (x) => x.dari).map((x) => ({ label: x.dari, dir: 'rtl' }))
      ])
    });

    // 3. Sound from glyph
    pool.push({
      type: 'sound',
      promptText: 'How is this letter pronounced?',
      glyph: L.dari,
      options: shuffle([
        { label: L.sound, isCorrect: true },
        ...sample(dariAlphabet, 3, L.sound, (x) => x.sound).map((x) => ({ label: x.sound }))
      ])
    });

    // 4. Positional form (joiners only — they have four distinct shapes)
    const forms = formsFor(L.dari);
    if (forms.length === 4) {
      const pos = forms[1 + Math.floor(Math.random() * 3)]; // initial/medial/final
      pool.push({
        type: 'form',
        promptText: `Which shows ${L.english} in the ${POSITION_LABELS[pos].toLowerCase()} position?`,
        options: shuffle(
          forms.map((p) => ({ label: shapeOf(L.dari, p), dir: 'rtl', isCorrect: p === pos }))
        )
      });
    }

    // 5. Audio: which letter does the word begin with? (recorded word starting with L)
    const startWord = pickExample(L.dari, 'initial', recordedSet);
    if (startWord && recordedSet && recordedSet.has(startWord) && Array.from(startWord)[0] === L.dari) {
      pool.push({
        type: 'audioStart',
        promptText: 'Listen — which letter does this word begin with?',
        audioWord: startWord,
        options: shuffle([
          { label: L.dari, dir: 'rtl', isCorrect: true },
          ...sample(dariAlphabet, 3, L.dari, (x) => x.dari).map((x) => ({ label: x.dari, dir: 'rtl' }))
        ])
      });
    }

    // 6. Spot the highlighted letter inside a real word
    const anyWord =
      pickExample(L.dari, 'medial', recordedSet) ||
      pickExample(L.dari, 'final', recordedSet) ||
      pickExample(L.dari, 'initial', recordedSet) ||
      pickExample(L.dari, 'isolated', recordedSet);
    if (anyWord) {
      pool.push({
        type: 'spot',
        promptText: 'Which letter is highlighted?',
        highlight: { word: anyWord, target: L.dari },
        options: shuffle([
          { label: L.english, isCorrect: true },
          ...sample(dariAlphabet, 3, L.english, (x) => x.english).map((x) => ({ label: x.english }))
        ])
      });
    }
  }

  return shuffle(pool).slice(0, Math.min(MAX_QUESTIONS, pool.length));
}

function LetterQuiz({ letters, recordedSet, onComplete }) {
  const questions = useMemo(() => buildQuestions(letters, recordedSet), [letters, recordedSet]);
  const [index, setIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [missedThis, setMissedThis] = useState(false);

  const q = questions[index];
  if (!q) {
    return <p className='quiz-empty'>No quiz questions available for this lesson yet.</p>;
  }

  const handlePick = (opt) => {
    if (selected && selected.isCorrect) return; // already moving on
    setSelected(opt);
    if (opt.isCorrect) {
      if (!missedThis) setCorrectCount((c) => c + 1);
      setTimeout(() => {
        if (index + 1 >= questions.length) {
          const total = questions.length;
          const got = correctCount + (missedThis ? 0 : 1);
          onComplete(Math.round((got / total) * 100));
        } else {
          setIndex((i) => i + 1);
          setSelected(null);
          setMissedThis(false);
        }
      }, 550);
    } else {
      setMissedThis(true);
    }
  };

  const stateFor = (opt) => {
    if (!selected) return '';
    if (opt === selected) return selected.isCorrect ? 'correct' : 'incorrect';
    return '';
  };

  return (
    <div className='question-section letter-quiz'>
      <div className='quiz-progress'>Question {index + 1} of {questions.length}</div>
      <p>{q.promptText}</p>

      {q.glyph && (
        <div className='quiz-glyph' lang='fa' dir='rtl'>{q.glyph}</div>
      )}
      {q.audioWord && (
        <button
          type='button'
          className='quiz-audio-btn'
          onClick={() => playLoud(audioUrlFor(q.audioWord))}
        >
          🔊 Play
        </button>
      )}
      {q.highlight && (
        <div className='quiz-highlight'>
          <ExampleWord word={q.highlight.word} target={q.highlight.target} recordedSet={recordedSet} />
        </div>
      )}

      {q.options.map((opt, i) => (
        <Option
          key={`${index}-${i}`}
          label={opt.label}
          state={stateFor(opt)}
          onClick={() => handlePick(opt)}
          dir={opt.dir}
          lang={opt.dir === 'rtl' ? 'fa' : undefined}
        />
      ))}
    </div>
  );
}

export default LetterQuiz;
